import React, { memo, useEffect, useState, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { isEqual, uniq } from 'lodash';
import dynamic from 'next/dynamic';
import compareAsc from 'date-fns/compareAsc';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'next-i18next';
import { Icon, IconButton } from 'components';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import { Button, Input, DropdownV2 } from 'components/form';
import Header from 'components/Header';
import { ButtonV3, TabRadioGroupV2 } from 'components/ComponentV2';
import Tooltip from 'components/Tooltip';
import { Row } from 'components/layout';
import AutoComplete from 'components/AutoComplete';
import { useNotification } from 'hooks/useNotification';
import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';
import { useApp } from 'hooks/useApp';
import { parseResponse } from 'lib/utils/parser';
import {
  PlaceAttributes,
  EventAttributes,
  EventRequestStep1,
} from 'lib/models/event';
import {
  locationOptions,
  LocationType,
  TouchpointType,
} from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { REGEX } from 'lib/utils/regex';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { TouchpointState } from 'lib/models/opportunity';
import {
  dateIsValid,
  formatDateToSave,
  formatDateToRetrieve,
  getRandomInt,
  isPastDate,
} from 'utils/format';
import { fetchAddressComponents } from 'utils/map';
import styles from '../Step.module.scss';

const DateTimePicker = dynamic(() => import('components/DateTimePicker'), {
  ssr: false,
});

export interface Step1Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: EventAttributes | null;
  onSave: (
    data: EventRequestStep1,
    isValueEdited: boolean,
    goToStep: number
  ) => void;
  removeBanner: (id: string) => Promise<unknown>;
  isLoading: boolean;
}

type TimezoneMappings = {
  [key: string]: string; // Assuming that both keys and values are strings
};

export const Step1: React.FC<Step1Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    onSave,
    removeBanner,
    isLoading,
  }: Step1Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );
    const router = useRouter();
    const notificationInstance = useNotification();
    const { onboarding } = router.query;
    const { isManager } = useAuth();
    const [t] = useTranslation('opportunity.posting');
    const [tcommon] = useTranslation('common');

    const { mutateEvents } = useCampaigns();

    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [bannerUrl, setBannerUrl] = useState('');
    const [title, setTitle] = useState('');
    const [touchpointableId, setTouchpointableId] = useState<string | null>(
      null
    );
    const [locationType, setLocationType] = useState<LocationType>(
      LocationType.IN_PERSON
    );
    const [placeAttributes, setPlaceAttributes] =
      useState<PlaceAttributes | null>(null);
    const datepickerContainerRef = useRef<HTMLDivElement | null>(null);
    const titlepickerContainerRef = useRef<HTMLDivElement | null>(null);

    const [showFurtherLocationDetailBox, setShowFurtherLocationDetailBox] =
      useState(false);
    const [addressDetails, setAddressDetails] = useState<string>();
    const [onlineLink, setOnlineLink] = useState<string>();

    const [isDateEmpty, setDateEmpty] = useState(false);
    const [startDate, setStartDate] = useState<{
      date: Date | null;
      showPicker: boolean;
    }>({ date: null, showPicker: true });
    const [endDate, setEndDate] = useState<{
      date: Date | null;
      showPicker: boolean;
    }>({ date: null, showPicker: false });
    const [deadline, setDeadline] = useState<{
      date: Date | null;
      showPicker: boolean;
    }>({ date: null, showPicker: false });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const { locationList } = useApp();
    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<EventRequestStep1>();

    const [timezone, setTimezone] = useState('Europe/London');
    const editPathName = isManager
      ? PAGE_ROUTES.EVENTS_EDIT
      : PAGE_ROUTES.CAMPAIGNS_EDIT;
    const [shouldScroll, setShouldScroll] = useState(false);

    useEffect(() => {
      if (touchpoint) {
        const {
          title,
          application_deadline,
          is_online,
          online_link,
          timezone: currentTimezone,
        } = touchpoint;
        const {
          id: touchpointableIdResponse,
          icon_url,
          start_date,
          end_date,
        } = parseResponse(touchpoint.touchpointable);
        const place = parseResponse(touchpoint.place) || null;
        const {
          id,
          address,
          address_details,
          google_identifier,
          latitude,
          longitude,
          city,
          country,
          address_locality,
          street_address,
          zipcode,
        } = place || {};

        if (icon_url) {
          setMediaFile(null);
          setBannerUrl(icon_url);
        } else {
          setRandomImage();
        }
        setTitle(title);
        setTouchpointableId(touchpointableIdResponse);

        if (is_online) {
          setLocationType(LocationType.VIRTUAL);
          setOnlineLink(online_link);
          setPlaceAttributes(null);
        } else {
          if (google_identifier) {
            setPlaceAttributes({
              id,
              google_identifier,
              address,
              latitude,
              longitude,
              city,
              country,
              address_locality,
              street_address,
              zipcode,
            });
          }
          if (address_details) {
            setShowFurtherLocationDetailBox(true);
            setAddressDetails(address_details);
          }
        }

        if (start_date) {
          setStartDate({
            date: formatDateToRetrieve(start_date),
            showPicker: true,
          });
        }

        if (end_date) {
          setEndDate({
            date: formatDateToRetrieve(end_date),
            showPicker: true,
          });
        }

        if (application_deadline) {
          setDeadline({
            date: formatDateToRetrieve(application_deadline),
            showPicker: true,
          });
        }

        if (currentTimezone) {
          setTimezone(currentTimezone);
        }

        setAvailableTouchpointData({
          application_deadline: application_deadline
            ? formatDateToSave(formatDateToRetrieve(application_deadline))
            : null,
          title,
          touchpointable_attributes: {
            id: touchpointableIdResponse,
            type: TouchpointType.Event,
            start_date: start_date
              ? formatDateToSave(formatDateToRetrieve(start_date))
              : null,
            end_date: end_date
              ? formatDateToSave(formatDateToRetrieve(end_date))
              : null,
            icon_link: icon_url,
            icon: undefined,
          },
          is_online,
          online_link,
          place_attributes: place,
          timezone: currentTimezone,
          country_id: timezoneCountryId,
        });
      } else {
        setRandomImage();
      }
    }, [touchpoint]);

    const timezoneCountryId = useMemo(() => {
      const selectedLoc = locationList.find((item) =>
        item?.country?.timezones?.includes(timezone)
      );
      return selectedLoc?.country?.id ?? '';
    }, [timezone, locationList]);

    const areValuesEdited = () => {
      if (touchpoint) {
        const {
          city: _city,
          country: _country,
          ...restPlace
        } = (placeAttributes as PlaceAttributes) || {};

        const selectedAttributes = {
          application_deadline: deadline.date
            ? formatDateToSave(deadline.date)
            : null,
          title,
          touchpointable_attributes: {
            id: touchpointableId || '',
            type: TouchpointType.Event,
            start_date: startDate.date
              ? formatDateToSave(startDate.date)
              : null,
            end_date: endDate.date ? formatDateToSave(endDate.date) : null,
            icon: undefined,
            icon_link: mediaFile ? imageUrl : bannerUrl,
          },
          is_online: locationType === LocationType.VIRTUAL ? true : false,
          online_link: onlineLink || null,
          place_attributes: placeAttributes
            ? {
                ...restPlace,
                address_details: addressDetails || null,
                city: placeAttributes?.city,
                country: placeAttributes?.country,
                zipcode: placeAttributes?.zipcode,
                street_address: placeAttributes?.street_address,
                address_locality: placeAttributes?.address_locality,
              }
            : null,
          timezone: timezone,
          country_id: timezoneCountryId,
        };
        return !isEqual(selectedAttributes, availableTouchpointData);
      } else {
        return !!title;
      }
    };

    const onClose = async () => {
      if (areValuesEdited()) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: false, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(
              isManager
                ? PAGE_ROUTES.DASHBOARD
                : PAGE_ROUTES.RECRUITER_DASHBOARD
            );
            return;
          }
          history.back();
        }
      } else {
        if (onboarding) {
          router.push(
            isManager ? PAGE_ROUTES.DASHBOARD : PAGE_ROUTES.RECRUITER_DASHBOARD
          );
          return;
        }
        mutateEvents();
        history.back();
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;

      if (touchpoint) {
        const { touchpointable, description } = touchpoint;
        const { event_category_ids } = parseResponse(touchpointable);
        if (
          (event_category_ids?.length === 0 || description.length === 0) &&
          goToStep === 3
        ) {
          notificationInstance.error({
            title: 'Details section not filled.',
            message: `Fill the Details section to move to Registrations`,
          });
          return;
        }
      }
      onSubmit({ checkRequired: true, goToStep });
    };

    useEffect(() => {
      if (startDate.date && dateIsValid(startDate.date)) {
        setDateEmpty(false);
      } else if (endDate.date && dateIsValid(endDate.date)) {
        setDateEmpty(false);
      } else if (deadline.date && !dateIsValid(deadline.date)) {
        setDateEmpty(false);
      }
    }, [startDate, endDate, deadline]);

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep = 2,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
    }) => {
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited() as boolean;
        const place = parseResponse(touchpoint?.place);
        const { id: placeAttributesId } = place || {};

        const data = {
          application_deadline: deadline.date
            ? formatDateToSave(deadline.date)
            : null,
          title,
          touchpointable_attributes: {
            id: touchpointableId || '',
            type: TouchpointType.Event,
            start_date: startDate.date
              ? formatDateToSave(startDate.date)
              : null,
            end_date: endDate.date ? formatDateToSave(endDate.date) : null,
            icon: mediaFile ? mediaFile : undefined,
            icon_link: mediaFile ? undefined : bannerUrl,
          },
          is_online: locationType === LocationType.VIRTUAL ? true : false,
          ...(locationType === LocationType.IN_PERSON && {
            remote_country_ids: [],
          }),
          timezone: timezone,
          country_id: timezoneCountryId,
          online_link: onlineLink,
          ...(placeAttributes && placeAttributes?.latitude
            ? {
                place_attributes: {
                  ...placeAttributes,
                  address_details: addressDetails,
                },
              }
            : {}),
          ...(!placeAttributes && placeAttributesId
            ? {
                place_attributes: { id: placeAttributesId, _destroy: true },
              }
            : {}),
          submitType,
        };
        onSave(data, isValueEdited, goToStep);
      };
      if (isDateEmpty || isStartDateInvalid) {
        setShouldScroll(true);
      }
      if (!title) {
        setShouldScroll(true);
      }

      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
        else if (!startDate.date) {
          setDateEmpty(true);
          return;
        } else if (startDate.date && !dateIsValid(startDate.date)) {
          setDateEmpty(true);
          return;
        } else if (endDate.date && !dateIsValid(endDate.date)) {
          setDateEmpty(true);
          return;
        } else if (deadline.date && !dateIsValid(deadline.date)) {
          setDateEmpty(true);
          return;
        } else if (startDate.date && isPastDate(startDate.date, timezone)) {
          setDateEmpty(true);
          return;
        } else if (deadline.date && isPastDate(deadline.date, timezone)) {
          setDateEmpty(true);
          return;
        } else if (
          deadline.date &&
          endDate.date &&
          compareAsc(endDate.date, deadline.date) === -1
        ) {
          setDateEmpty(true);
          return;
        } else if (
          deadline.date &&
          startDate.date &&
          compareAsc(startDate.date, deadline.date) === -1
        ) {
          setDateEmpty(true);
          return;
        }
      }
      onSaveRequest();
    };

    const setRandomImage = () => {
      const number = getRandomInt(10, 21);
      setBannerUrl(
        `${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/images/huzzle-gradient-${number}.jpg`
      );
    };

    const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      setMediaFile(files[0]);
    };

    const timezoneList = useMemo(() => {
      const uniqueLocations = uniqBy(locationList, (obj) => obj?.country?.name);
      return uniq(
        uniqueLocations
          .map(({ country }) => country?.timezones)
          ?.flatMap((time) => time) || []
      );
    }, [locationList]);

    const timezoneMappings = useMemo(() => {
      let mappings: TimezoneMappings = {};
      const uniqueLocations = uniqBy(locationList, (obj) => obj?.country?.name);
      uniqueLocations.forEach((loc) => {
        mappings = { ...mappings, ...loc?.timezoneMapping };
      });
      return mappings;
    }, [locationList]);

    const timezoneOptions = useMemo(() => {
      return timezoneList.map((data, index) => ({
        id: (index + 1).toString(),
        label: data ? `${data} ${timezoneMappings[data] || ''}` : '',
        value: data || '',
      }));
    }, [timezoneList, timezoneMappings]);

    const imageUrl = mediaFile ? URL.createObjectURL(mediaFile) : bannerUrl;

    const isInvalid = !title || isDateEmpty;

    const isStartDateInvalid =
      startDate.date && endDate.date
        ? compareAsc(startDate.date, endDate.date) === 1
        : false;
    useEffect(() => {
      if (shouldScroll) {
        if (isDateEmpty || isStartDateInvalid) {
          datepickerContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }
        if (!title) {
          titlepickerContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }

        setShouldScroll(false);
      }
    }, [shouldScroll, title, isDateEmpty, isStartDateInvalid]);
    const isValidEndDate =
      startDate.date && endDate.date
        ? compareAsc(endDate.date, startDate.date) === -1
        : false;

    const timezoneLabel = useMemo(() => {
      return (
        timezoneOptions.find((data) => data.value === timezone)?.label || ''
      );
    }, [timezoneOptions, timezone]);

    const minDataValue = useMemo(() => {
      const date = new Date();
      return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    }, [timezone]);

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onNavigate(2)}
          onNextLoading={isLoading}
          className={styles.header}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <div className={styles.imageContainer}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Upload image"
                  className={styles.image}
                />
              ) : null}
              <input
                type="file"
                accept="image/*"
                id="upload-image"
                hidden
                onChange={onImageChange}
              />
              <Row className={styles.imageButtonRow}>
                <Tooltip
                  position="top"
                  color="dark"
                  content="It should be smaller than 2MB. Optimal resolution is 1360x640."
                  className={styles.imageTooltip}
                  containerClassName={styles.imageTooltipContainer}
                >
                  <Button
                    type="button"
                    color="white"
                    size="medium"
                    className={styles.imageButton}
                  >
                    <label htmlFor="upload-image" style={{ cursor: 'pointer' }}>
                      {t('event-posting.change-cover-photo')}
                    </label>
                  </Button>
                </Tooltip>
                {(mediaFile ||
                  (bannerUrl && router.pathname === editPathName)) && (
                  <IconButton
                    size="small"
                    iconName="trash"
                    className={styles.imageButtonDelete}
                    onClick={() => {
                      setMediaFile(null);
                      setRandomImage();
                      if (touchpoint && touchpoint?.id) {
                        removeBanner(touchpoint?.id);
                      }
                    }}
                    type="button"
                  />
                )}
              </Row>
            </div>
            <section className={styles.section} ref={titlepickerContainerRef}>
              <main className={styles.main}>
                <p className={styles.title}>{t('event-posting.basic-info')}</p>
                <p className={styles.desc}>
                  {t('event-posting.basic-info-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <Input
                    label={t('event-posting.title')}
                    placeholder={t('event-posting.event-input-placeholder')}
                    maxLength={120}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    isValid={!(isSubmitted && !title)}
                    className={cn(styles.input)}
                  />
                  {isSubmitted && !title ? (
                    <p className={styles.required}>
                      {t('event-posting.required')}
                    </p>
                  ) : null}
                </div>
              </main>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('event-posting.location')}{' '}
                  <span>({t('event-posting.optional')})</span>
                </p>
                <p className={styles.desc}>
                  {t('event-posting.location-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <TabRadioGroupV2
                  title=""
                  options={locationOptions}
                  value={locationType}
                  onChange={(value: string) =>
                    setLocationType(value as LocationType)
                  }
                />
                <div className={styles.gap}></div>
                {locationType === LocationType.IN_PERSON ? (
                  <>
                    <AutoComplete
                      placeAttributes={placeAttributes}
                      fetchAddressComponents={fetchAddressComponents}
                      setPlaceAttributes={setPlaceAttributes}
                    />
                    <Row align="center" className={styles.infoRow}>
                      <Icon iconName="information" size="xsmall" />
                      <p className={styles.info}>
                        {t('event-posting.add-location-info')}
                      </p>
                    </Row>
                    {!showFurtherLocationDetailBox ? (
                      <>
                        <div className={styles.gap}></div>
                        <ButtonV3
                          type="button"
                          startIcon="plus"
                          color="secondary"
                          size="small"
                          onClick={() => setShowFurtherLocationDetailBox(true)}
                        >
                          {t('event-posting.instructions-cta')}
                        </ButtonV3>
                      </>
                    ) : (
                      <>
                        <div className={styles.gap}></div>
                        <div className={styles.inputContainer}>
                          <Input
                            label={t('event-posting.further-location-details')}
                            placeholder={t(
                              'event-posting.add-location-details'
                            )}
                            value={addressDetails}
                            onChange={(event) =>
                              setAddressDetails(event.target.value)
                            }
                            isValid={true}
                            className={styles.input}
                          />
                          <div className={styles.removeContainer}>
                            <IconButton
                              iconName="close"
                              size="xsmall"
                              onClick={() => {
                                setShowFurtherLocationDetailBox(false);
                                setAddressDetails('');
                              }}
                              className={cn(styles.remove)}
                            />
                          </div>
                        </div>
                        <Row align="center" className={styles.infoRow}>
                          <Icon iconName="information" size="xsmall" />
                          <p className={styles.info}>
                            {t('event-posting.add-specefic-details-info')}
                          </p>
                        </Row>
                      </>
                    )}
                  </>
                ) : (
                  <div>
                    <Input
                      label={t('event-posting.event-link')}
                      placeholder={t(
                        'event-posting.add-event-link-placeholder'
                      )}
                      value={onlineLink}
                      onChange={(event) => setOnlineLink(event.target.value)}
                      isValid={onlineLink ? REGEX.URL.test(onlineLink) : true}
                      className={styles.input}
                    />
                    <Row align="center" className={styles.infoRow}>
                      <Icon iconName="information" size="xsmall" />
                      <p className={styles.info}>
                        {t('event-posting.add-event-link')}
                      </p>
                    </Row>
                  </div>
                )}
              </main>
            </section>
            <section className={styles.section} ref={datepickerContainerRef}>
              <main className={styles.main}>
                <p className={styles.title}>{t('event-posting.date-time')}</p>
                <p className={styles.desc}>
                  {t('event-posting.date-time-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <Row
                  className={cn(styles.datepickerRow, styles.inputContainer)}
                >
                  <DateTimePicker
                    label={t('event-posting.start-date-time')}
                    min={minDataValue}
                    value={startDate.date as Date}
                    onChange={(value) => {
                      if (value)
                        setStartDate({ ...startDate, date: new Date(value) });
                      else setDateEmpty(true);
                    }}
                    className={styles.datepicker}
                    isValid={!isDateEmpty && !isStartDateInvalid}
                    helperText={
                      isDateEmpty
                        ? t('event-posting.required')
                        : isStartDateInvalid
                        ? t('event-posting.select-valid-date')
                        : ''
                    }
                  />
                </Row>

                {endDate.showPicker ? (
                  <>
                    <div className={styles.gap}></div>
                    <Row
                      className={cn(
                        styles.datepickerRow,
                        styles.inputContainer
                      )}
                    >
                      <DateTimePicker
                        label={t('event-posting.end-date-time')}
                        min={
                          startDate.date
                            ? (startDate.date as Date)
                            : minDataValue
                        }
                        value={endDate.date as Date}
                        onChange={(value) =>
                          setEndDate({ ...endDate, date: new Date(value) })
                        }
                        className={styles.datepicker}
                        isValid={!isDateEmpty && !isValidEndDate}
                        helperText={
                          isDateEmpty
                            ? t('event-posting.required')
                            : isValidEndDate
                            ? t('event-posting.select-valid-date')
                            : ''
                        }
                      />
                      <IconButton
                        iconName="close"
                        size="xsmall"
                        onClick={() =>
                          setEndDate({
                            date: null,
                            showPicker: false,
                          })
                        }
                        className={cn(styles.remove)}
                      />
                    </Row>
                  </>
                ) : null}

                {deadline.showPicker ? (
                  <>
                    <div className={styles.gap}></div>
                    <Row
                      className={cn(
                        styles.datepickerRow,
                        styles.inputContainer
                      )}
                    >
                      <DateTimePicker
                        label={t('event-posting.registration-deadline-label')}
                        min={minDataValue}
                        max={startDate.date as Date}
                        value={deadline.date as Date}
                        onChange={(value) =>
                          setDeadline({ ...deadline, date: new Date(value) })
                        }
                        className={styles.datetime}
                        isValid={!isDateEmpty}
                        helperText={
                          isDateEmpty ? t('event-posting.required') : ''
                        }
                      />
                      <IconButton
                        iconName="close"
                        size="xsmall"
                        onClick={() =>
                          setDeadline({
                            date: null,
                            showPicker: false,
                          })
                        }
                        className={cn(styles.remove)}
                      />
                    </Row>
                  </>
                ) : null}
                <div className={styles.gap}></div>
                <p className={styles.headingLabel}>
                  {t('event-posting.time-zone')}
                </p>
                <DropdownV2
                  label={t('event-posting.select-time-zone')}
                  title=""
                  value={timezone}
                  onChange={(value) => setTimezone(value)}
                  options={timezoneOptions}
                  selectedValueLabel={timezoneLabel}
                  isValid={true}
                  className={styles.dropdown}
                />
                {!endDate.showPicker || !deadline.showPicker ? (
                  <>
                    <div className={styles.gap}></div>
                    <Row className={styles.buttonRow}>
                      {!endDate.showPicker ? (
                        <ButtonV3
                          size="small"
                          color="secondary"
                          startIcon="plus"
                          onClick={() =>
                            setEndDate({
                              ...endDate,
                              showPicker: true,
                            })
                          }
                          disabled={!startDate.date}
                        >
                          {t('event-posting.add-end-date-cta')}
                        </ButtonV3>
                      ) : null}
                      {!deadline.showPicker ? (
                        <ButtonV3
                          size="small"
                          color="secondary"
                          startIcon="plus"
                          onClick={() =>
                            setDeadline({
                              ...deadline,
                              showPicker: true,
                            })
                          }
                          disabled={!startDate.date}
                        >
                          {t('event-posting.add-registration-deadline-cta')}
                        </ButtonV3>
                      ) : null}
                    </Row>
                  </>
                ) : null}
              </main>
            </section>
            <ButtonV3
              type="submit"
              onClick={() => onSubmit({ checkRequired: true })}
              className={styles.next}
              isLoading={isLoading}
              disabled={isSubmitted && isInvalid}
            >
              {tcommon('next')}
            </ButtonV3>
          </div>
        </div>
      </>
    );
  }
);

Step1.displayName = 'Step1';
